import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, Server, Activity, AlertTriangle, RefreshCw, CheckCircle2, XCircle, Clock, HardDrive, Save, Archive, Mail, Send, ShieldAlert } from 'lucide-react';
import { api } from '../../api/client';

type Health = {
  status: 'ok' | 'degraded';
  service: string;
  version: string;
  nodeVersion: string;
  timestamp: string;
  startedAt: string;
  uptimeSec: number;
  database: { status: 'ok' | 'error'; latencyMs: number; error: string | null };
  memory: { rss: number; heapUsed: number; heapTotal: number; external: number };
};

type Metrics = {
  tenants: { total: number; suspended: number };
  locations: { total: number };
  users: { total: number; active: number };
  operators: { total: number; active: number };
  sales: { total: number; last24h: number };
  sysadmins: { total: number; active: number };
};

type LicenseStatus = {
  expired: Array<{ id: string; tenantId: string; tenantName: string | null; moduleCode: string; validUntil: string; isTrial: boolean }>;
  expiringSoon: Array<{ id: string; tenantId: string; tenantName: string | null; moduleCode: string; validUntil: string; isTrial: boolean }>;
};

type CertStatus = {
  expired: number;
  expiringSoon: number;
  byTenant: Array<{
    id: string; tenantId: string; tenantName: string | null; locationName: string | null;
    isProduction: boolean; isActive: boolean; expiresAt: string; daysLeft: number;
  }>;
};

type AuditSummary = {
  windowDays: number;
  total: number;
  byAction: Array<{ action: string; count: number }>;
};

type BackupStatus = {
  backupDir: string;
  exists: boolean;
  count: number;
  lastBackup: { name: string; sizeBytes: number; createdAt: string } | null;
  backups: Array<{ name: string; sizeBytes: number; createdAt: string }>;
};

export function SystemPage() {
  const [health, setHealth] = useState<Health | null>(null);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [licenseStatus, setLicenseStatus] = useState<LicenseStatus | null>(null);
  const [auditSummary, setAuditSummary] = useState<AuditSummary | null>(null);
  const [backupStatus, setBackupStatus] = useState<BackupStatus | null>(null);
  const [certStatus, setCertStatus] = useState<CertStatus | null>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backingUp, setBackingUp] = useState(false);
  const [backupResult, setBackupResult] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [h, m, l, a, b, c] = await Promise.all([
        api.get<Health>('/admin/system/health'),
        api.get<Metrics>('/admin/system/metrics'),
        api.get<LicenseStatus>('/admin/system/licenses-status'),
        api.get<AuditSummary>('/admin/system/audit-summary'),
        api.get<BackupStatus>('/admin/system/backup-status').catch(() => null),
        api.get<CertStatus>('/admin/system/certificates-status').catch(() => null),
      ]);
      setHealth(h);
      setMetrics(m);
      setLicenseStatus(l);
      setAuditSummary(a);
      setBackupStatus(b);
      setCertStatus(c);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, []);

  const runBackup = async () => {
    setBackingUp(true);
    setBackupResult(null);
    try {
      const result = await api.post<{ filename: string; sizeBytes: number; durationMs: number }>('/admin/system/backup');
      setBackupResult(`Backup uspešan: ${result.filename} (${formatBytes(result.sizeBytes)}, ${result.durationMs}ms)`);
      // Refresh backup status
      const b = await api.get<BackupStatus>('/admin/system/backup-status');
      setBackupStatus(b);
    } catch (e: any) {
      setBackupResult(`Greška: ${e?.data?.message || e?.message || 'unknown'}`);
    } finally {
      setBackingUp(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000); // auto-refresh every 30s
    return () => clearInterval(id);
  }, [refresh]);

  const formatUptime = (sec: number) => {
    const days = Math.floor(sec / 86400);
    const hours = Math.floor((sec % 86400) / 3600);
    const mins = Math.floor((sec % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const formatDate = (iso: string) => new Date(iso).toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Sistem</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Health, metrike, licence, sertifikati i audit pregled
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={refresh} disabled={loading} style={btnSecondary}>
          <RefreshCw size={13} /> Osveži
        </button>
      </header>

      {error && (
        <div style={errorBox}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      <EmailSection />

      {/* Health overview */}
      {health && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 14, marginBottom: 24 }}>
          <Card icon={<Server size={18} />} title="API Status" color={health.status === 'ok' ? '#10b981' : '#f59e0b'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {health.status === 'ok' ? <CheckCircle2 size={18} style={{ color: '#10b981' }} /> : <AlertTriangle size={18} style={{ color: '#f59e0b' }} />}
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-strong)', textTransform: 'uppercase' }}>{health.status}</span>
            </div>
            <Detail label="Verzija" value={`v${health.version}`} />
            <Detail label="Node" value={health.nodeVersion} />
          </Card>

          <Card icon={<Database size={18} />} title="Baza" color={health.database.status === 'ok' ? '#10b981' : '#dc2626'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {health.database.status === 'ok' ? <CheckCircle2 size={18} style={{ color: '#10b981' }} /> : <XCircle size={18} style={{ color: '#dc2626' }} />}
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-strong)' }}>
                {health.database.status === 'ok' ? 'Konektovana' : 'Greška'}
              </span>
            </div>
            <Detail label="Ping" value={`${health.database.latencyMs} ms`} />
            {health.database.error && <Detail label="Error" value={health.database.error} />}
          </Card>

          <Card icon={<Clock size={18} />} title="Uptime" color="#60a5fa">
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-text-strong)' }}>
              {formatUptime(health.uptimeSec)}
            </div>
            <Detail label="Pokrenut" value={formatDate(health.startedAt)} />
          </Card>

          <Card icon={<HardDrive size={18} />} title="Memorija" color="#a78bfa">
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-text-strong)' }}>
              {health.memory.heapUsed} <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500 }}>/ {health.memory.heapTotal} MB</span>
            </div>
            <Detail label="RSS" value={`${health.memory.rss} MB`} />
            <Detail label="External" value={`${health.memory.external} MB`} />
          </Card>
        </div>
      )}

      {/* Metrics */}
      {metrics && (
        <>
          <h2 style={sectionTitle}>Statistika</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 14, marginBottom: 24 }}>
            <Stat label="Klijenti" value={metrics.tenants.total} sub={metrics.tenants.suspended > 0 ? `${metrics.tenants.suspended} suspendovano` : undefined} />
            <Stat label="Lokacije" value={metrics.locations.total} />
            <Stat label="Klijent useri" value={metrics.users.active} sub={`${metrics.users.total} ukupno`} />
            <Stat label="Operateri" value={metrics.operators.active} sub={`${metrics.operators.total} ukupno`} />
            <Stat label="Računi (24h)" value={metrics.sales.last24h} sub={`${metrics.sales.total} ukupno`} />
            <Stat label="Sysadmin nalozi" value={metrics.sysadmins.active} sub={`${metrics.sysadmins.total} ukupno`} />
          </div>
        </>
      )}

      {/* License alerts */}
      {licenseStatus && (
        <>
          <h2 style={sectionTitle}>Licence - upozorenja</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
            <Card icon={<XCircle size={18} />} title={`Istekle (${licenseStatus.expired.length})`} color="#dc2626">
              {licenseStatus.expired.length === 0
                ? <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Nema isteklih licenci</div>
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflow: 'auto' }}>
                    {licenseStatus.expired.slice(0, 10).map((l) => (
                      <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <div>
                          <div style={{ color: 'var(--color-text-strong)', fontWeight: 600 }}>{l.tenantName || '-'}</div>
                          <div style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{l.moduleCode}{l.isTrial && ' · trial'}</div>
                        </div>
                        <div style={{ color: '#fca5a5', fontFamily: 'monospace', fontSize: 11 }}>{formatDate(l.validUntil)}</div>
                      </div>
                    ))}
                  </div>
                )}
            </Card>

            <Card icon={<AlertTriangle size={18} />} title={`Ističu (30 dana) - ${licenseStatus.expiringSoon.length}`} color="#f59e0b">
              {licenseStatus.expiringSoon.length === 0
                ? <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Nema licenci pred istekom</div>
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflow: 'auto' }}>
                    {licenseStatus.expiringSoon.slice(0, 10).map((l) => (
                      <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <div>
                          <div style={{ color: 'var(--color-text-strong)', fontWeight: 600 }}>{l.tenantName || '-'}</div>
                          <div style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{l.moduleCode}{l.isTrial && ' · trial'}</div>
                        </div>
                        <div style={{ color: '#fcd34d', fontFamily: 'monospace', fontSize: 11 }}>{formatDate(l.validUntil)}</div>
                      </div>
                    ))}
                  </div>
                )}
            </Card>
          </div>
        </>
      )}

      {/* Fiskalni sertifikati - isti prag kao podsetnici firmi (30 dana), aktivni sertifikati */}
      {certStatus && (() => {
        const aktivni = certStatus.byTenant.filter((c) => c.isActive);
        const istekli = aktivni.filter((c) => c.daysLeft <= 0);
        const isticu = aktivni.filter((c) => c.daysLeft > 0 && c.daysLeft <= 30);
        const lista = (rows: CertStatus['byTenant'], prazno: string, boja: string) => rows.length === 0
          ? <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{prazno}</div>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflow: 'auto' }}>
              {rows.map((c) => (
                <div key={c.id} onClick={() => navigate(`/tenants/${c.tenantId}`)} title="Otvori firmu"
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                  <div>
                    <div style={{ color: 'var(--color-text-strong)', fontWeight: 600 }}>{c.tenantName || '-'}</div>
                    <div style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{c.locationName || 'lokacija'}{!c.isProduction && ' · test'}</div>
                  </div>
                  <div style={{ textAlign: 'right', color: boja, fontFamily: 'monospace', fontSize: 11 }}>
                    <div>{c.daysLeft < 0 ? `pre ${-c.daysLeft} d.` : c.daysLeft === 0 ? 'danas' : `za ${c.daysLeft} d.`}</div>
                    <div>{new Date(c.expiresAt).toLocaleDateString('sr-RS')}</div>
                  </div>
                </div>
              ))}
            </div>
          );
        return (
          <>
            <h2 style={sectionTitle}>Fiskalni sertifikati - upozorenja</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 14, marginBottom: 24 }}>
              <Card icon={<ShieldAlert size={18} />} title={`Istekli (${istekli.length})`} color="#dc2626">
                {lista(istekli, 'Nema isteklih sertifikata', '#fca5a5')}
              </Card>
              <Card icon={<AlertTriangle size={18} />} title={`Ističu (30 dana) - ${isticu.length}`} color="#f59e0b">
                {lista(isticu, 'Nema sertifikata pred istekom', '#fcd34d')}
              </Card>
            </div>
          </>
        );
      })()}

      {/* Backup */}
      {backupStatus && (
        <>
          <h2 style={sectionTitle}>Backup</h2>
          <Card icon={<Archive size={18} />} title={`Backup direktorijum: ${backupStatus.backupDir}`} color="#10b981">
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                {backupStatus.lastBackup ? (
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                    Poslednji backup: <strong style={{ color: 'var(--color-text)' }}>{backupStatus.lastBackup.name}</strong> · {formatBytes(backupStatus.lastBackup.sizeBytes)} · {formatDate(backupStatus.lastBackup.createdAt)}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Još nema backup-a</div>
                )}
                <div style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 4 }}>{backupStatus.count} fajlova ukupno</div>
              </div>
              <button
                onClick={runBackup}
                disabled={backingUp}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '9px 16px', borderRadius: 6, border: 'none',
                  background: '#10b981', color: '#fff',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  opacity: backingUp ? 0.5 : 1,
                }}
              >
                <Save size={14} /> {backingUp ? 'Backup u toku...' : 'Pokreni backup'}
              </button>
            </div>
            {backupResult && (
              <div style={{
                fontSize: 12, padding: 10, borderRadius: 6,
                background: backupResult.startsWith('Greška') ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
                color: backupResult.startsWith('Greška') ? '#fca5a5' : '#6ee7b7',
              }}>
                {backupResult}
              </div>
            )}
            {backupStatus.backups.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflow: 'auto' }}>
                {backupStatus.backups.map((b) => (
                  <div key={b.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '4px 8px', background: 'var(--color-bg)', borderRadius: 4 }}>
                    <span style={{ fontFamily: 'monospace', color: 'var(--color-text)' }}>{b.name}</span>
                    <span style={{ color: 'var(--color-text-muted)' }}>{formatBytes(b.sizeBytes)} · {formatDate(b.createdAt)}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--color-text-faint)' }}>
              Backup koristi <code style={{ fontFamily: 'monospace', background: 'var(--color-bg)', padding: '1px 5px', borderRadius: 3 }}>pg_dump</code> komandu. Mora biti dostupna u PATH-u na serveru.
            </div>
          </Card>
        </>
      )}

      {/* Audit summary */}
      {auditSummary && (
        <>
          <h2 style={sectionTitle}>Audit aktivnost (poslednjih {auditSummary.windowDays} dana)</h2>
          <Card icon={<Activity size={18} />} title={`Ukupno: ${auditSummary.total} događaja`} color="#a78bfa">
            {auditSummary.byAction.length === 0
              ? <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Nema audit događaja</div>
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {auditSummary.byAction.slice(0, 10).map((a) => (
                    <div key={a.action} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ flex: 1, fontSize: 12, color: 'var(--color-text)', fontFamily: 'monospace' }}>{a.action}</div>
                      <div style={{ width: 200, height: 6, background: 'var(--color-bg)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          width: `${(a.count / auditSummary.byAction[0].count) * 100}%`,
                          height: '100%',
                          background: 'var(--color-primary)',
                        }} />
                      </div>
                      <div style={{ width: 50, fontSize: 12, color: 'var(--color-text-strong)', fontWeight: 600, textAlign: 'right', fontFamily: 'monospace' }}>{a.count}</div>
                    </div>
                  ))}
                </div>
              )}
          </Card>
        </>
      )}
    </div>
  );
}

type SmtpInfo = {
  configured: boolean; host: string | null; port: number; secure: boolean; user: string | null; passwordSet: boolean;
  from: string; fromName: string;
};
type SmtpCheck = { ok: boolean; stub: boolean; error?: string };
type SendResult = { ok: true; stub?: boolean; messageId?: string } | { ok: false; error: string };

/**
 * Mejl (nova lozinka, potvrde, pozivnice): da li je SMTP podešen i da li prijava
 * na server prolazi, plus probni mejl - provera produkcije bez SSH-a na server.
 */
function EmailSection() {
  const [info, setInfo] = useState<(SmtpInfo & { webAppUrl: string; check: SmtpCheck }) | null>(null);
  const [loading, setLoading] = useState(false);
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ check: SmtpCheck; sent: SendResult } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setInfo(await api.get('/admin/system/email')); setError(null); }
    catch (e: any) { setError(e?.data?.message || e?.message || 'Greška'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const sendTest = async () => {
    setSending(true); setResult(null); setError(null);
    try { setResult(await api.post('/admin/system/email/test', { to })); }
    catch (e: any) { setError(e?.data?.message || e?.message || 'Greška'); }
    finally { setSending(false); }
  };

  const ok = info?.check.ok;
  const status = !info ? '…' : ok ? 'Radi' : info.check.stub ? 'Nije podešen' : 'Ne radi';
  return (
    <div style={{ marginBottom: 24 }}>
      <Card icon={<Mail size={18} />} title="Mejl (SMTP)" color={!info ? '#60a5fa' : ok ? '#10b981' : '#dc2626'}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {info && (ok ? <CheckCircle2 size={18} style={{ color: '#10b981' }} /> : <XCircle size={18} style={{ color: '#dc2626' }} />)}
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-strong)' }}>{status}</span>
          <div style={{ flex: 1 }} />
          <button onClick={load} disabled={loading} style={btnSecondary}><RefreshCw size={13} /> Proveri</button>
        </div>
        {info?.check.error && <div style={{ fontSize: 12, color: '#fca5a5', fontFamily: 'monospace', wordBreak: 'break-word' }}>{info.check.error}</div>}
        {info && (
          <>
            <Detail label="Server" value={info.host ? `${info.host}:${info.port}${info.secure ? ' (SSL)' : ' (STARTTLS)'}` : '- nije podešen -'} />
            <Detail label="Korisnik" value={info.user ?? '-'} />
            <Detail label="Lozinka" value={info.passwordSet ? 'postavljena' : 'NIJE postavljena'} />
            <Detail label="Pošiljalac" value={`${info.fromName} <${info.from}>`} />
            <Detail label="Linkovi u mejlu" value={info.webAppUrl} />
          </>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="adresa za probni mejl"
            style={{ flex: '1 1 220px', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text-strong)', fontSize: 13 }} />
          <button onClick={sendTest} disabled={sending || !to.trim()} style={{ ...btnSecondary, opacity: sending || !to.trim() ? 0.6 : 1 }}>
            <Send size={13} /> {sending ? 'Šaljem…' : 'Pošalji probni mejl'}
          </button>
        </div>
        {result && (
          <div style={{ fontSize: 12, color: result.sent.ok && !('stub' in result.sent && result.sent.stub) ? '#10b981' : '#fca5a5', wordBreak: 'break-word' }}>
            {!result.sent.ok
              ? `Nije poslat: ${result.sent.error}`
              : result.sent.stub
                ? 'Nije poslat - SMTP nije podešen (server je mejl samo upisao u log).'
                : `Predat SMTP serveru (${result.sent.messageId ?? ''}). Ako ne stigne za par minuta, proverite spam i SPF/DKIM domena pošiljaoca.`}
          </div>
        )}
        {error && <div style={{ fontSize: 12, color: '#fca5a5' }}>{error}</div>}
      </Card>
    </div>
  );
}

function Card({ icon, title, color, children }: { icon: React.ReactNode; title: string; color: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color }}>
        {icon}
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 0, overflowWrap: 'anywhere' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--color-text-strong)', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value.toLocaleString('sr-RS')}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--color-text-muted)' }}>
      <span>{label}</span>
      <span style={{ fontFamily: 'monospace', color: 'var(--color-text)' }}>{value}</span>
    </div>
  );
}

const sectionTitle: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 12px 0' };
const btnSecondary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
